import { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import type { StoreConfig } from '../types/api';
import CustomerAccess from './CustomerAccess';
import StoreSettings from './StoreSettings';
import ProductPanel from './ProductPanel';
import './PanelDashboard.css';

type Tab = 'dashboard' | 'orders' | 'customers' | 'cancelled' | 'customer-access' | 'settings' | 'products';

interface Props {
  storeConfig: StoreConfig | null;
  onStoreSaved: (cfg: StoreConfig) => void;
  onBackToCatalog: () => void;
  onLogout: () => void;
}

type PanelOrder = {
  id: string;
  orderNumber: number;
  status: string;
  customerName: string;
  customerCnpj: string;
  sellerId?: string | null;
  sellerName?: string | null;
  total: number;
  notes: string;
  createdAt: string;
  importedAt?: string | null;
  items: Array<{ productId: string; productName: string; quantity: number; unitPrice: number; total: number }>;
};

type PanelCustomer = {
  id: string;
  cnpj: string;
  name: string;
  tradeName?: string | null;
  email?: string | null;
  phone?: string | null;
  active: boolean;
  lastLoginAt?: string | null;
  ordersCount: number;
};

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString('pt-BR');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function PanelDashboard({ storeConfig, onStoreSaved, onBackToCatalog, onLogout }: Props) {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [loginUser, setLoginUser] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [panelUser, setPanelUser] = useState('');
  const [orders, setOrders] = useState<PanelOrder[]>([]);
  const [customers, setCustomers] = useState<PanelCustomer[]>([]);
  const [sellerNames, setSellerNames] = useState<Record<string, string>>({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const [originFilter, setOriginFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<PanelOrder | null>(null);

  function normalizeText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  function getOrderOrigin(order: PanelOrder): 'Loja' | 'Online' {
    return order.importedAt ? 'Loja' : 'Online';
  }

  const filteredCustomers = useMemo(() => {
    const term = customerSearch.trim();
    if (!term) return customers;

    const normalizedTerm = normalizeText(term);
    const numericTerm = term.replace(/\D/g, '');

    return customers.filter(customer => {
      const combinedText = normalizeText([
        customer.cnpj,
        customer.name,
        customer.tradeName ?? '',
        customer.email ?? '',
        customer.phone ?? '',
      ].join(' '));

      if (combinedText.includes(normalizedTerm)) return true;

      const combinedDigits = [customer.cnpj, customer.phone ?? ''].join('').replace(/\D/g, '');
      return numericTerm.length > 0 && combinedDigits.includes(numericTerm);
    });
  }, [customerSearch, customers]);

  async function loadData() {
    setLoadingData(true);
    setError('');
    try {
      const [orderData, customerData, sellerData] = await Promise.all([
        api.getPanelOrders(),
        api.getPanelCustomers(),
        api.getPanelSellers(),
      ]);
      setOrders(orderData);
      setCustomers(customerData);
      setSellerNames(Object.fromEntries(sellerData.map(seller => [seller.id, seller.name])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar painel');
    } finally {
      setLoadingData(false);
    }
  }

  function resolveSellerLabel(order: PanelOrder): string {
    return order.sellerName || (order.sellerId ? sellerNames[order.sellerId] : '') || 'Vendedor nao identificado';
  }

  useEffect(() => {
    let alive = true;

    api.getPanelMe()
      .then(res => {
        if (!alive) return;
        setPanelUser(res.user.user);
        return loadData();
      })
      .catch(() => {
        if (!alive) return;
        api.clearPanelToken();
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoginError('');

    try {
      const res = await api.panelLogin(loginUser, loginPassword);
      api.setPanelToken(res.token);
      const me = await api.getPanelMe();
      setPanelUser(me.user.user);
      await loadData();
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Erro ao entrar no painel');
    }
  }

  function handleLogout() {
    api.clearPanelToken();
    onLogout();
  }

  function openOrderDetails(order: PanelOrder) {
    setSelectedOrder(order);
  }

  function closeOrderDetails() {
    setSelectedOrder(null);
  }

  function printOrder80mm(order: PanelOrder) {
    const win = window.open('', '_blank', 'width=420,height=700');
    if (!win) return;

    const lines = order.items.map(item => `
      <tr>
        <td>${escapeHtml(String(item.quantity))}x</td>
        <td>${escapeHtml(item.productName)}</td>
        <td>${formatCurrency(item.total)}</td>
      </tr>
    `).join('');

    win.document.open();
    win.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Venda #${order.orderNumber}</title>
          <style>
            @page { size: 80mm auto; margin: 4mm; }
            html, body { width: 80mm; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; font-size: 11px; color: #111; }
            .ticket { width: 72mm; padding: 0; }
            h1 { font-size: 14px; margin: 0 0 4px; }
            .muted { color: #555; font-size: 10px; margin-bottom: 6px; }
            .row { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
            .divider { border-top: 1px dashed #999; margin: 6px 0; }
            table { width: 100%; border-collapse: collapse; }
            td { vertical-align: top; padding: 2px 0; }
            td:last-child { text-align: right; white-space: nowrap; }
            .totals { font-weight: 700; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="ticket">
            <h1>${escapeHtml(storeConfig?.name || 'Villamix Express')}</h1>
            <div class="muted">Reimpressao 80 mm</div>
            <div class="row"><span>Venda</span><strong>#${escapeHtml(String(order.orderNumber))}</strong></div>
            <div class="row"><span>Cliente</span><span>${escapeHtml(order.customerName)}</span></div>
            <div class="row"><span>CNPJ</span><span>${escapeHtml(order.customerCnpj)}</span></div>
            ${resolveSellerLabel(order) ? `<div class="row"><span>Vendedor</span><span>${escapeHtml(resolveSellerLabel(order))}</span></div>` : ''}
            <div class="row"><span>Status</span><span>${escapeHtml(order.status)}</span></div>
            <div class="row"><span>Data</span><span>${escapeHtml(formatDate(order.createdAt))}</span></div>
            <div class="divider"></div>
            <table>
              <tbody>
                ${lines}
              </tbody>
            </table>
            <div class="divider"></div>
            <div class="row totals"><span>Total</span><span>${escapeHtml(formatCurrency(order.total))}</span></div>
            ${order.notes ? `<div class="divider"></div><div>Obs: ${escapeHtml(order.notes)}</div>` : ''}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
  }

  const cancelledOrders = orders.filter(order => order.status === 'CANCELADO');
  const filteredOrders = useMemo(() => {
    const fromTime = orderDateFrom ? new Date(`${orderDateFrom}T00:00:00`).getTime() : null;
    const toTime = orderDateTo ? new Date(`${orderDateTo}T23:59:59.999`).getTime() : null;
    const normalizedOrigin = originFilter.trim().toLowerCase();

    return orders.filter(order => {
      const createdAt = new Date(order.createdAt).getTime();
      if (fromTime !== null && createdAt < fromTime) return false;
      if (toTime !== null && createdAt > toTime) return false;
      if (normalizedOrigin && getOrderOrigin(order).toLowerCase() !== normalizedOrigin) return false;
      return true;
    });
  }, [orders, orderDateFrom, orderDateTo, originFilter]);

  const filteredCancelledOrders = useMemo(
    () => filteredOrders.filter(order => order.status === 'CANCELADO'),
    [filteredOrders],
  );

  if (loading) {
    return <div className="panel-shell panel-loading">Carregando painel...</div>;
  }

  if (!panelUser) {
    return (
      <div className="panel-shell panel-login-shell">
        <button className="panel-back" onClick={onBackToCatalog}>Voltar ao site</button>
        <form className="panel-login-card" onSubmit={handleLogin}>
          <h1>Painel</h1>
          <p>Entre com o usuario administrador para gerenciar pedidos e clientes.</p>
          <label>
            Usuario
            <input value={loginUser} onChange={e => setLoginUser(e.target.value)} />
          </label>
          <label>
            Senha
            <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} />
          </label>
          {loginError && <p className="panel-error">{loginError}</p>}
          <button type="submit">Entrar no painel</button>
        </form>
      </div>
    );
  }

  return (
    <div className="panel-shell">
      <header className="panel-header">
        <div>
          <h1>Painel Administrativo</h1>
          <p>{panelUser}</p>
        </div>
        <div className="panel-header-actions">
          <button onClick={onBackToCatalog}>Ir para loja</button>
          <button onClick={handleLogout}>Sair</button>
        </div>
      </header>

      <nav className="panel-tabs">
        {(['dashboard', 'orders', 'customers', 'customer-access', 'settings', 'products', 'cancelled'] as Tab[]).map(item => (
          <button key={item} className={tab === item ? 'active' : ''} onClick={() => setTab(item)}>
            {item === 'dashboard' && 'Dashboard'}
            {item === 'orders' && 'Pedidos'}
            {item === 'customers' && 'Clientes'}
            {item === 'customer-access' && 'Acesso do Cliente'}
            {item === 'settings' && 'Configurações'}
            {item === 'products' && 'Produtos'}
            {item === 'cancelled' && 'Cancelados'}
          </button>
        ))}
      </nav>

      {error && <div className="panel-error-banner">{error}</div>}

      {loadingData ? (
        <div className="panel-loading">Carregando dados...</div>
      ) : (
        <main className="panel-content">
          {tab === 'dashboard' && (
            <section className="panel-grid">
              <article className="panel-card"><strong>{orders.length}</strong><span>Pedidos</span></article>
              <article className="panel-card"><strong>{customers.length}</strong><span>Clientes</span></article>
              <article className="panel-card"><strong>{cancelledOrders.length}</strong><span>Cancelados</span></article>
              <article className="panel-card"><strong>{storeConfig?.name || 'Loja'}</strong><span>Empresa</span></article>
            </section>
          )}

          {tab === 'orders' && (
            <section className="panel-table-wrap">
              <div className="panel-table-head">
                <h2>Pedidos Recebidos</h2>
                <div className="panel-date-filters">
                  <label>
                    <span>De</span>
                    <input type="date" value={orderDateFrom} onChange={e => setOrderDateFrom(e.target.value)} />
                  </label>
                  <label>
                    <span>Até</span>
                    <input type="date" value={orderDateTo} onChange={e => setOrderDateTo(e.target.value)} />
                  </label>
                  <label>
                    <span>Origem</span>
                    <select value={originFilter} onChange={e => setOriginFilter(e.target.value)}>
                      <option value="">Todas</option>
                      <option value="Loja">Loja</option>
                      <option value="Online">Online</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className="panel-date-clear"
                    onClick={() => {
                      setOrderDateFrom('');
                      setOrderDateTo('');
                      setOriginFilter('');
                    }}
                  >
                    Limpar
                  </button>
                </div>
              </div>
              <table className="panel-table">
                <thead>
                  <tr><th>Nº</th><th>Cliente</th><th>Status</th><th>Origem</th><th>Total</th><th>Data</th></tr>
                </thead>
                <tbody>
                  {filteredOrders.map(order => (
                    <tr key={order.id}>
                      <td>
                        <button type="button" className="panel-order-link" onClick={() => openOrderDetails(order)}>
                          #{order.orderNumber}
                        </button>
                      </td>
                      <td>{order.customerName}</td>
                      <td>{order.status}</td>
                      <td>{getOrderOrigin(order)}</td>
                      <td>{formatCurrency(order.total)}</td>
                      <td>{formatDate(order.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {tab === 'customers' && (
            <section className="panel-table-wrap">
              <div className="panel-section-header">
                <div>
                  <h2>Clientes</h2>
                  <p>Busque por CNPJ, nome, fantasia, email ou telefone. {filteredCustomers.length} de {customers.length} encontrados.</p>
                </div>
                <label className="panel-filter">
                  <span>Buscar cliente</span>
                  <input
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    placeholder="Digite para filtrar"
                  />
                </label>
              </div>
              <table className="panel-table">
                <thead>
                  <tr><th>CNPJ</th><th>Nome</th><th>Fantasia</th><th>Email</th><th>Pedidos</th><th>Ultimo acesso</th></tr>
                </thead>
                <tbody>
                  {filteredCustomers.map(customer => (
                    <tr key={customer.id}>
                      <td>{customer.cnpj}</td>
                      <td>{customer.name}</td>
                      <td>{customer.tradeName || '-'}</td>
                      <td>{customer.email || '-'}</td>
                      <td>{customer.ordersCount}</td>
                      <td>{customer.lastLoginAt ? formatDate(customer.lastLoginAt) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {tab === 'customer-access' && (
            <CustomerAccess customers={customers} />
          )}

          {tab === 'cancelled' && (
            <section className="panel-table-wrap">
              <div className="panel-table-head">
                <h2>Pedidos Cancelados</h2>
                <div className="panel-date-filters">
                  <label>
                    <span>De</span>
                    <input type="date" value={orderDateFrom} onChange={e => setOrderDateFrom(e.target.value)} />
                  </label>
                  <label>
                    <span>Até</span>
                    <input type="date" value={orderDateTo} onChange={e => setOrderDateTo(e.target.value)} />
                  </label>
                  <label>
                    <span>Origem</span>
                    <select value={originFilter} onChange={e => setOriginFilter(e.target.value)}>
                      <option value="">Todas</option>
                      <option value="Loja">Loja</option>
                      <option value="Online">Online</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className="panel-date-clear"
                    onClick={() => {
                      setOrderDateFrom('');
                      setOrderDateTo('');
                      setOriginFilter('');
                    }}
                  >
                    Limpar
                  </button>
                </div>
              </div>
              <table className="panel-table">
                <thead>
                  <tr><th>Nº</th><th>Cliente</th><th>Origem</th><th>Total</th><th>Data</th></tr>
                </thead>
                <tbody>
                  {filteredCancelledOrders.map(order => (
                    <tr key={order.id}>
                      <td>
                        <button type="button" className="panel-order-link" onClick={() => openOrderDetails(order)}>
                          #{order.orderNumber}
                        </button>
                      </td>
                      <td>{order.customerName}</td>
                      <td>{getOrderOrigin(order)}</td>
                      <td>{formatCurrency(order.total)}</td>
                      <td>{formatDate(order.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {tab === 'settings' && (
            <StoreSettings onSaved={onStoreSaved} />
          )}

          {tab === 'products' && (
            <ProductPanel />
          )}

          {selectedOrder && (
            <div className="panel-order-modal" role="dialog" aria-modal="true" aria-label={`Pedido ${selectedOrder.orderNumber}`}>
              <button type="button" className="panel-order-modal-backdrop" onClick={closeOrderDetails} aria-label="Fechar detalhe do pedido" />
              <section className="panel-order-modal-card">
                <div className="panel-order-modal-header">
                  <div>
                    <h2>Venda #{selectedOrder.orderNumber}</h2>
                    <p>{selectedOrder.customerName}</p>
                    <span className="panel-order-modal-seller">Vendedor: {resolveSellerLabel(selectedOrder)}</span>
                  </div>
                  <button type="button" className="panel-order-modal-close" onClick={closeOrderDetails}>Fechar</button>
                </div>

                <div className="panel-order-modal-meta">
                  <span><strong>Status:</strong> {selectedOrder.status}</span>
                  <span><strong>Data:</strong> {formatDate(selectedOrder.createdAt)}</span>
                  <span><strong>CNPJ:</strong> {selectedOrder.customerCnpj}</span>
                  <span><strong>Vendedor:</strong> {resolveSellerLabel(selectedOrder)}</span>
                  <span><strong>Origem:</strong> {getOrderOrigin(selectedOrder)}</span>
                </div>

                <div className="panel-order-modal-items">
                  {selectedOrder.items.map(item => (
                    <div key={item.productId} className="panel-order-modal-item">
                      <div>
                        <strong>{item.productName}</strong>
                        <span>{item.quantity}x {formatCurrency(item.unitPrice)}</span>
                      </div>
                      <strong>{formatCurrency(item.total)}</strong>
                    </div>
                  ))}
                </div>

                <div className="panel-order-modal-footer">
                  <strong>Total: {formatCurrency(selectedOrder.total)}</strong>
                  <div className="panel-order-modal-actions">
                    <button type="button" onClick={() => printOrder80mm(selectedOrder)}>Reimprimir 80 mm</button>
                    <button type="button" className="panel-order-modal-close" onClick={closeOrderDetails}>Fechar</button>
                  </div>
                </div>
              </section>
            </div>
          )}
        </main>
      )}
    </div>
  );
}
